import { FlexRowAlignCenter } from "@/components/Flex";
import HtmlText from "@/components/HtmlText";
import { ASSETS_URL } from "@/config/deployConstants";
import { FlashcardSet } from "@knowt/syncing/graphql/schema";
import Image from "next/image";
import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import styles from "./flashcardsToPrint.module.css";

type FlashcardsToPrintProps = {
    flashcardSet: FlashcardSet;
};

export type FlashcardsToPrintHandle = {
    print: () => void;
};

const FlashcardsToPrint = forwardRef(
    (
        {
            flashcardSet,
        }: FlashcardsToPrintProps & {
            ref?: React.RefObject<unknown>;
        },
        ref
    ) => {
        const [isPrinted, setIsPrinted] = useState(false);

        const componentRef = useRef<HTMLDivElement>(null);

        const printFlashcards = useReactToPrint({
            contentRef: componentRef,
            documentTitle: flashcardSet.title || "Knowt",
            bodyClass: styles.printedBodyClass,
            pageStyle: `
            @page {
            size: A4;
            margin: 4rem 0;
            }`,
            onAfterPrint: () => setIsPrinted(false),
        });

        const areFlashcardsUnavailable = () => {
            return (
                !flashcardSet.flashcards ||
                flashcardSet.flashcards.every(flashcard => {
                    const { term, definition } = flashcard ?? {};
                    return (term === null || term === undefined) && (definition === null || definition === undefined);
                })
            );
        };

        useImperativeHandle(ref, () => ({
            print: () => {
                if (areFlashcardsUnavailable()) {
                    throw new Error("Flashcards are not yet loaded");
                }

                setIsPrinted(true);

                // We are using a timeout here because we need to wait for the
                // component's content to be rendered before we can print it.
                setTimeout(() => {
                    printFlashcards();
                }, 100);
            },
        }));

        const title = flashcardSet.title || "Flashcard Set";

        if (!isPrinted) {
            return null;
        }

        return (
            <div
                ref={componentRef}
                className={styles.container}
                data-testid="auto-id-70798ea8-137d-4ffa-99fb-df5c4faea41d">
                <div
                    className={styles.logo}
                    data-testid="auto-id-0a590747-0a0b-41ad-ba71-49954a96bc99">
                    <Image
                        src={`${ASSETS_URL}/images/PDFLogo.svg`}
                        alt={"knowt logo"}
                        width={80}
                        height={80}
                        style={{ objectFit: "contain" }}
                    />
                </div>
                <div
                    className={styles.title}
                    data-testid="auto-id-6bda7d38-5f8c-4b26-ac48-294d1f53dce5">{title}</div>
                <table
                    className={styles.table}
                    data-testid="auto-id-0c1b066b-c82b-4588-919f-e8c64497cfe1">
                    {flashcardSet.flashcards?.map(flashcard => (
                        <tr
                            className={styles.row}
                            key={flashcard?.flashcardId}
                            data-testid="auto-id-e6570900-8632-48f0-928f-7877d9ff0d65">
                            <td
                                className={styles.cell}
                                data-testid="auto-id-76aa7159-5a09-42c1-92e8-223710442918">
                                <FlexRowAlignCenter style={{ gap: "1rem" }}>
                                    {flashcard?.secondaryImage && (
                                        // biome-ignore lint/nursery/noImgElement: <explanation>
                                        (<img
                                            src={flashcard.secondaryImage}
                                            alt="the term"
                                            height="100%"
                                            style={{ maxWidth: 150, maxHeight: 200 }}
                                            data-testid="auto-id-4af7a460-1629-4389-97f0-86cb355c380f" />)
                                    )}
                                    <HtmlText text={flashcard?.term} />
                                </FlexRowAlignCenter>
                            </td>
                            <td
                                className={styles.cell}
                                style={{ borderLeft: "1px dashed #000" }}
                                data-testid="auto-id-d5c3ac8a-2b38-4cbd-a31e-02ec4c7b9766">
                                <FlexRowAlignCenter style={{ gap: "1rem", justifyContent: "space-between" }}>
                                    <HtmlText text={flashcard?.definition} />
                                    {flashcard?.image && (
                                        // biome-ignore lint/nursery/noImgElement: <explanation>
                                        (<img
                                            src={flashcard.image}
                                            alt="the definition"
                                            height="100%"
                                            style={{ maxWidth: 150, maxHeight: 200 }}
                                            data-testid="auto-id-c7d7da45-e98c-44d6-96d0-58e48c287149" />)
                                    )}
                                </FlexRowAlignCenter>
                            </td>
                        </tr>
                    ))}
                </table>
            </div>
        );
    }
);

FlashcardsToPrint.displayName = "FlashcardsToPrint";
export default FlashcardsToPrint;
